import { httpQuery } from '@luminovo/data-stack';
import { searchParam, useSetSearch } from '@luminovo/design-system';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { JSX } from 'react';
import { z } from 'zod/v4';

import AssemblyPage from '@/areas/assemblies/Assemblies/AssemblyPage';
import { singleSubassemblyOf } from '@/areas/assemblies/Assemblies/components/singleSubassembly';
import { assemblyTabs } from '@/domain/assembly/assemblyFrontendTypes';
import { assembliesModulePermissions } from '@/shared/permissions/pagePermissions';
import type { PageStaticData } from '@/shared/routing';

// The BOM tab forwards on the assembly and its paths from the BOM, which the loader reads with the tab's queries.
export const Route = createFileRoute('/_authenticated/next/projects/$projectId/assemblies/$assemblyId/')({
    staticData: {
        access: { permissions: ['view:assembly'], modulePermissions: assembliesModulePermissions },
        chrome: 'none',
    } satisfies PageStaticData,
    validateSearch: z.object({
        tab: searchParam.enum(assemblyTabs, 'optional'),
        monitoring: searchParam.boolean('optional'),
        isMonitoringOpen: searchParam.boolean('optional'),
        currentParentAssemblyId: z.string().optional(),
    }),
    loaderDeps: ({ search }) => ({ bomTab: (search.tab ?? 'Bom') === 'Bom' }),
    loader: async ({ context: { queryClient, session }, params: { projectId, assemblyId }, deps }) => {
        if (!deps.bomTab) {
            return;
        }
        const [
            {
                items: [assembly],
            },
            { multi_parent_items: parents },
        ] = await Promise.all([
            queryClient.ensureQueryData(
                httpQuery('POST /assemblies/bulk', {
                    requestBody: { ids: [assemblyId] },
                    queryParams: { with_aggregations: false },
                }),
            ),
            queryClient.ensureQueryData(
                httpQuery('GET /assemblies/:assemblyId/parents', { pathParams: { assemblyId } }),
            ),
        ]);
        // A PCB-only assembly has no BOM. Checked first, as its subassembly's BOM would be the wrong page too.
        if (assembly?.type.type === 'PcbOnly' && session.isFeatureFlagEnabled('pcb_module')) {
            throw redirect({
                to: '/projects/$projectId/bom/assembly/$assemblyId/pcb',
                params: { projectId, assemblyId },
                replace: true,
            });
        }
        const subassemblyId = singleSubassemblyOf(assembly, parents);
        if (subassemblyId !== undefined) {
            throw redirect({
                to: '/projects/$projectId/bom/assembly/$assemblyId',
                params: { projectId, assemblyId: subassemblyId },
                search: { currentParentAssemblyId: assemblyId },
                replace: true,
            });
        }
    },
    component: RouteComponent,
});

function RouteComponent(): JSX.Element {
    const pathParams = Route.useParams();
    const currentParentAssemblyId = Route.useSearch({ select: (search) => search.currentParentAssemblyId });
    const tab = Route.useSearch({ select: (search) => search.tab });
    const monitoring = Route.useSearch({ select: (search) => search.monitoring });
    const setSearch = useSetSearch();
    return (
        <AssemblyPage
            {...pathParams}
            currentParentAssemblyId={currentParentAssemblyId}
            tab={tab}
            monitoringDeepLink={{ isOpen: monitoring, onSearchChange: setSearch }}
        />
    );
}
